import React, { forwardRef, ReactNode } from 'react';
import Button from '@discover-components/ui/button';
import Dots from '@discover-components/ui/dots';
import { cn } from '@discover-lib/utils';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import { useAnalytics } from '@/components/analytics/hooks/useAnalytics';

interface OAuthButtonProps {
  loading?: boolean;
  disabled?: boolean;
  active?: boolean;
  unavailable?: boolean;
  complete?: boolean;
  method?: string;
  onClick?: () => void;
  Icon?: React.ComponentType;
  expanded?: boolean;
  boldLabel?: boolean;
}

const OAuthButton = forwardRef<HTMLButtonElement, OAuthButtonProps>(
  (
    {
      loading,
      disabled,
      active,
      unavailable,
      complete,
      method,
      onClick,
      Icon,
      expanded,
      boldLabel,
    },
    ref
  ) => {
    const { track } = useAnalytics();
    let msg: ReactNode = `Continue with ${method}`;
    if (unavailable) {
      msg = `${method} login currently unavailable :(`;
    } else if (complete) {
      msg = null;
    } else if (active) {
      msg = <Dots className="ml-1" />;
    }

    const handleClick = () => {
      track('button_clicked', {
        button_name: `${method} social login`,
        button_type: 'open modal',
        button_copy: `Continue with ${method}`,
      });
      onClick?.();
    };

    return (
      <TrackProvider button_name={`${method} social login`} button_type="open modal">
        <Button
          ref={ref}
          loading={loading}
          disabled={disabled || unavailable}
          onClick={handleClick}
          data-testid={`${method.toLowerCase()}-login`}
          kind={method.toLowerCase() as 'facebook' | 'google' | 'apple'}
          className={cn(
            'flex h-[54px] flex-1 items-center justify-center px-[13px] md:px-md',
            expanded && 'w-full gap-2'
          )}
          type="button">
          {Icon && <Icon />}
          <span
            className={cn(
              'm-0 text-sm md:inline-block',
              expanded && 'inline-block',
              expanded && !boldLabel ? 'font-normal' : 'font-bold',
              {
                hidden: !expanded && !active,
              }
            )}>
            {msg}
          </span>
        </Button>
      </TrackProvider>
    );
  }
);

OAuthButton.displayName = 'OAuthButton';

export default OAuthButton;
